import { createApi } from "@reduxjs/toolkit/query/react";
import { baseAuthQuery } from "../base/baseApi";
import { ApiResponse } from "../base/apiResponse";
import { FilterDto, TeacherResponse, TeacherWithPhotoResponse, TeacherReportResponse } from "../../models/dtos";

export const teachersApi = createApi({
  reducerPath: "teachersApi",
  baseQuery: baseAuthQuery("teachers"),
  tagTypes: ["Teacher"],
  endpoints: (builder) => ({
    getTeachers: builder.query<ApiResponse<TeacherResponse[]>, Partial<FilterDto>>({
      query: (query) => {
        return {
          url: "",
          method: "POST",
          body: query,
        };
      },
      providesTags: ["Teacher"],
    }),
    getAllTeachers: builder.query<ApiResponse<TeacherResponse[]>, void>({
      query: () => "",
      providesTags: ["Teacher"],
    }),
    getTeachersReport: builder.query<ApiResponse<TeacherReportResponse[]>, void>({
      query: () => "report",
      providesTags: ["Teacher"],
    }),
    getTeacher: builder.query<ApiResponse<TeacherWithPhotoResponse>, number>({
      queryFn: async (teacherId, _queryApi, _extraOptions, baseQuery) => {
        if (Number.isNaN(teacherId)) {
          return {
            data: { data: {} } as ApiResponse<TeacherWithPhotoResponse>,
            error: undefined,
          };
        }
        const response = await baseQuery({
          url: `/${teacherId}`,
          method: "GET",
        });
        if (response.error) {
          return { error: response.error };
        }
        return { data: response.data as ApiResponse<TeacherWithPhotoResponse> };
      },
      providesTags: (_result, _error, teacherId) => {
        return [{ type: "Teacher", id: teacherId }];
      },
    }),
    // Update the mutation to handle the new response type (teacherId as int)
    createOrUpdateTeacher: builder.mutation<ApiResponse<number>, Partial<TeacherResponse>>({
      query: (body) => ({ url: "CreateOrUpdate", method: "POST", body }),
      invalidatesTags: ["Teacher"],
    }),
    deleteTeacher: builder.mutation<ApiResponse<boolean>, number>({
      query: (id) => ({ url: `Delete/${id}`, method: "GET" }),
      invalidatesTags: ["Teacher"],
    }),
    updatePicture: builder.mutation<ApiResponse<boolean>, { teacherId: number; picture: File }>({
      query: ({ teacherId, picture }) => {
        const formData = new FormData();
        formData.append("picture", picture);
        return {
          url: `${teacherId}/UpdatePicture`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: (_result, _error, { teacherId }) => {
        return [{ type: "Teacher", id: teacherId }];
      },
    }),
  }),
});

export const {
  useGetTeachersQuery,
  useGetAllTeachersQuery,
  useGetTeachersReportQuery,
  useGetTeacherQuery,
  useCreateOrUpdateTeacherMutation,
  useDeleteTeacherMutation,
  useUpdatePictureMutation,
} = teachersApi;
